import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import GoogleIcon from "@mui/icons-material/Google"
import MailOutlineIcon from "@mui/icons-material/MailOutline"
import MicrosoftMUIIcon from "@mui/icons-material/Microsoft"
import Button from "@mui/material/Button"
import ButtonGroup from "@mui/material/ButtonGroup"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Link from "@mui/material/Link"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useScript } from "usehooks-ts"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import SegmentTracker from "../../../common/tracking"
import { SnackbarPayload, openSnackbar } from "../../../reducers/snackbar"
import GmailIcon from "../../../static/images/email-provider-icons/gmail-icon.svg"
import MicrosoftIcon from "../../../static/images/email-provider-icons/microsoft.png"
import { isConcierge } from "../../users/accountRole"
import { selectProfile } from "../../users/reducer"
import {
  retrieveEmailAuthorizationStatus,
  selectEmailAuthorizationLoading,
  selectEmailAuthorizationStatus,
  selectEmailProviderAuthorized,
} from "../reducer"
import { SETTINGS_TABS, createSettingsTabURL } from "../settings"

export default function ConfirmConnectEmailDialog({ open, onClose }) {
  const [selectedProvider, setSelectedProvider] = useState(GoogleAuthProvider)
  const [providerMenuRef, setProviderMenuRef] = useState(null)
  const providers = useEmailProviders()

  function handleDialogClose() {
    onClose(false)
  }

  function closeProviderMenu() {
    setProviderMenuRef(null)
  }

  function handleProviderSelected(provider) {
    setSelectedProvider(provider)
    closeProviderMenu()
  }

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="xs" fullWidth>
      <DialogContent>
        <Column sx={{ color: "text.secondary" }}>
          <Row sx={{ justifyContent: "center", gap: 2 }}>
            <img src={GmailIcon} alt="Gmail" style={{ width: "48px" }} />
            <img
              src={MicrosoftIcon}
              alt="Microsoft/Outlook"
              style={{ width: "36px" }}
            />
          </Row>
          <Typography
            variant="h4"
            sx={{ mt: 4, textAlign: "center", color: "text.primary" }}
          >
            Email not connected yet
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            You need to connect your email account so we can use your identity
            to do the following:
          </Typography>
          <ol style={{ paddingInlineStart: 24, fontSize: "14px" }}>
            <li>Send emails to candidates.</li>
            <li>
              Create calendar events on your calendar when you schedule events
              with candidates.
            </li>
          </ol>
        </Column>
      </DialogContent>
      <DialogActions variant="balanced">
        <Button variant="outlined" color="inherit" onClick={handleDialogClose}>
          Cancel
        </Button>
        <ButtonGroup variant="contained" color="primary" size="small">
          <Button
            startIcon={<selectedProvider.muiIcon />}
            onClick={() => onClose(true, selectedProvider)}
            fullWidth
          >
            Connect
          </Button>
          <Button
            size="small"
            onClick={(e) => setProviderMenuRef(e.currentTarget)}
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>
        <Menu
          open={!!providerMenuRef}
          anchorEl={providerMenuRef}
          onClose={closeProviderMenu}
          anchorOrigin={{
            horizontal: "right",
            vertical: "bottom",
          }}
          transformOrigin={{
            horizontal: "right",
            vertical: "top",
          }}
        >
          {providers.map((provider) => (
            <MenuItem
              key={provider.id}
              onClick={() => handleProviderSelected(provider)}
            >
              <ListItemIcon>
                <provider.muiIcon />
              </ListItemIcon>
              <ListItemText>{provider.name}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </DialogActions>
    </Dialog>
  )
}

export function useConnectEmailDialogRequirements(authCallback = () => {}) {
  const [confirmConnectEmailDialogOpen, setConfirmConnectEmailDialogOpen] =
    useState(false)
  const emailAuthorization = useSelector(selectEmailAuthorizationStatus)
  const emailAuthorized = useSelector(selectEmailProviderAuthorized)
  const emailAuthorizationLoading = useSelector(selectEmailAuthorizationLoading)
  const profile = useSelector(selectProfile)
  const concierge = isConcierge(profile)
  const navigate = useNavigate()
  const [tokenClient, setTokenClient] = useState(null)
  const apiScriptStatus = useScript("https://apis.google.com/js/api.js")
  const gsiScriptStatus = useScript("https://accounts.google.com/gsi/client")
  const axios = useAuthorizedAxiosClient()
  const { initiateMSLogin } = useMicrosoftAuthUtils(authCallback)
  const handleEmailProviderConnected =
    useHandleEmailProviderConnected(authCallback)

  function openConfirmConnectEmailDialog() {
    if (concierge) {
      navigate(createSettingsTabURL(SETTINGS_TABS.configureEmail))
      return
    }
    setConfirmConnectEmailDialogOpen(true)
  }

  function updateProviderConnection(provider) {
    if (concierge) {
      navigate(createSettingsTabURL(SETTINGS_TABS.configureEmail))
      return
    }
    switch (provider.id) {
      case GoogleAuthProvider.id:
        if (tokenClient) {
          tokenClient.requestCode()
        }
        break
      case MicrosoftProvider.id:
        initiateMSLogin()
        break
      default:
        break
    }
  }

  function handleConfirmConnectGmailDialogClosed(confirmed, selectedProvider) {
    if (confirmed) {
      updateProviderConnection(selectedProvider)
    }
    setConfirmConnectEmailDialogOpen(false)
  }

  useEffect(
    () => {
      if (concierge) {
        return
      }
      if (emailAuthorizationLoading || !window?.google?.accounts?.oauth2) {
        authCallback(MAIL_PROVIDER_AUTH_STATUS.gmailFail)
        return
      }
      const client = window.google.accounts.oauth2.initCodeClient({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        scope: [
          "openid",
          "https://www.googleapis.com/auth/userinfo.email",
          "https://www.googleapis.com/auth/gmail.send",
          "https://www.googleapis.com/auth/calendar.events",
        ].join(" "),
        ux_mode: "popup",
        callback: (response) => {
          if (response.error) {
            return
          }
          axios
            .post("/workflows/api/gmail-authcode/", {
              code: response.code,
              referrer: window.location.origin,
            })
            .then(() => {
              handleEmailProviderConnected(
                MAIL_PROVIDER_AUTH_STATUS.gmailSuccess,
                "Gmail connected",
                "Google account connected successfully"
              )
            })
            .catch(() => authCallback(MAIL_PROVIDER_AUTH_STATUS.gmailFail))
        },
      })
      setTokenClient(client)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [emailAuthorization, apiScriptStatus, gsiScriptStatus]
  )

  return {
    confirmConnectEmailDialogOpen,
    openConfirmConnectEmailDialog,
    updateProviderConnection,
    handleConfirmConnectGmailDialogClosed,
    emailAuthorized: emailAuthorized && !emailAuthorizationLoading,
  }
}

function useHandleEmailProviderConnected(authCallback) {
  const dispatch = useDispatch()

  function handleEmailProviderConnected(
    mailProviderStatus,
    segmentEventName,
    successMessage
  ) {
    authCallback(mailProviderStatus)
    dispatch(retrieveEmailAuthorizationStatus())
    dispatch(openSnackbar(new SnackbarPayload(successMessage)))
    SegmentTracker.track(segmentEventName)
  }

  return handleEmailProviderConnected
}

export function useMicrosoftAuthUtils(authCallback = () => {}) {
  const profile = useSelector(selectProfile)

  const scopes = [
    "openid",
    "offline_access",
    "email",
    "profile",
    "User.Read",
    "Calendars.ReadWrite",
    "Mail.ReadWrite",
    "Mail.Send",
  ]

  function initiateMSLogin() {
    const urlSearchParams = new URLSearchParams()
    urlSearchParams.append("client_id", process.env.REACT_APP_MSAL_CLIENT_ID)
    urlSearchParams.append("response_type", "code")
    urlSearchParams.append("response_mode", "query")
    urlSearchParams.append("scope", scopes.join(" "))
    urlSearchParams.append("prompt", "consent")
    urlSearchParams.append("state", profile.id)
    urlSearchParams.append(
      "redirect_uri",
      new URL(
        "/workflows/ms-login-redirect/",
        process.env.REACT_APP_API_URL
      ).toString()
    )
    window.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${urlSearchParams.toString()}`
  }

  return {
    initiateMSLogin,
  }
}

export const MAIL_PROVIDER_AUTH_STATUS = {
  gmailSuccess: 0,
  gmailFail: 1,
  msIDSuccess: 2,
  msIDPopupWindowError: 3,
}

class EmailProvider {
  constructor(id, name, icon, muiIcon) {
    this.id = id
    this.name = name
    this.icon = icon
    this.muiIcon = muiIcon
  }
}

export const ZohoAuthProvider = new EmailProvider(
  "zoho",
  "Company email",
  null,
  MailOutlineIcon
)
export const GoogleAuthProvider = new EmailProvider(
  "gmail",
  "Google Auth",
  GmailIcon,
  GoogleIcon
)
export const MicrosoftProvider = new EmailProvider(
  "ms",
  "Microsoft",
  MicrosoftIcon,
  MicrosoftMUIIcon
)
export const EMAIL_PROVIDERS = [GoogleAuthProvider, MicrosoftProvider]

export function useEmailProviders() {
  return useMemo(() => {
    return EMAIL_PROVIDERS
  }, [])
}
